import React from 'react';

import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedCaseStudy = ({ study }) => {
  const getThemeClasses = () => {
    switch (study?.category?.toLowerCase()) {
      case 'events':
        return {
          gradient: 'from-rose-600 to-pink-600',
          accent: 'text-rose-600',
          button: 'bg-rose-600 hover:bg-rose-700'
        };
      case 'real estate':
        return {
          gradient: 'from-blue-600 to-slate-600',
          accent: 'text-blue-600',
          button: 'bg-blue-600 hover:bg-blue-700'
        };
      case 'marketing':
        return {
          gradient: 'from-orange-600 to-red-600',
          accent: 'text-orange-600',
          button: 'bg-orange-600 hover:bg-orange-700'
        };
      case 'design fiction':
        return {
          gradient: 'from-emerald-600 to-teal-600',
          accent: 'text-emerald-600',
          button: 'bg-emerald-600 hover:bg-emerald-700'
        };
      default:
        return {
          gradient: 'from-gray-800 to-gray-900',
          accent: 'text-primary',
          button: 'bg-primary hover:bg-primary/90'
        };
    }
  };

  const themeClasses = getThemeClasses();

  return (
    <div className="relative overflow-hidden rounded-2xl bg-white shadow-2xl">
      <div className="grid lg:grid-cols-2 gap-0">
        {/* Content Side */}
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${themeClasses?.gradient} flex items-center justify-center`}>
                <Icon name={study?.icon} size={24} color="white" />
              </div>
              <div>
                <span className={`text-sm font-semibold ${themeClasses?.accent} uppercase tracking-wider`}>
                  Featured Case Study
                </span>
                <div className="text-xs text-gray-500 mt-1">
                  {study?.category} • {study?.duration}
                </div>
              </div>
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {study?.title}
            </h2>

            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {study?.description}
            </p>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              {study?.metrics?.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className={`text-2xl font-bold ${themeClasses?.accent} mb-1`}>
                    {metric?.value}
                  </div>
                  <div className="text-sm text-gray-500">
                    {metric?.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Client Testimonial */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-4">
                <Image
                  src={study?.client?.avatar}
                  alt={study?.client?.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <p className="text-gray-700 italic mb-3">
                    "{study?.testimonial}"
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {study?.client?.name}
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-500 text-sm">
                      {study?.client?.title}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                className={`${themeClasses?.button} text-white flex-1`}
                iconName="Play"
                iconPosition="left"
              >
                Watch Case Study
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                iconName="Download"
                iconPosition="left"
              >
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Visual Side */}
        <div className="relative">
          <div className="aspect-square lg:aspect-auto lg:h-full relative overflow-hidden">
            <Image
              src={study?.image}
              alt={study?.title}
              className="w-full h-full object-cover"
            />
            
            {/* Overlay */}
            <div className={`absolute inset-0 bg-gradient-to-br ${themeClasses?.gradient} opacity-20`} />
            
            {/* Play Button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer">
                <Icon name="Play" size={32} color="white" />
              </div>
            </div>

            {/* Awards Badge */}
            {study?.awards && (
              <div className="absolute top-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 text-center">
                  <Icon name="Award" size={20} className={themeClasses?.accent} />
                  <div className="text-xs font-semibold text-gray-900 mt-1">
                    Award Winner
                  </div>
                </div>
              </div>
            )}

            {/* Category Badge */}
            <div className="absolute bottom-6 left-6">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${themeClasses?.accent} bg-white/90 backdrop-blur-sm`}>
                {study?.category}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedCaseStudy;